import { isCapacitorPlatform } from "@utils/specific/capacitor";
import { SUPPORTED_LANGUAGES, UserAccountType } from "@common/constants";
import { useFormContext } from "react-hook-form";
import { getUserFromLocalStorage } from "@utils/membership";
import { DefaultPath } from "./defaultRoutePathsEnum";
import { THEME_SETTINGS } from "@theme-settings";
import { localizationService } from "@state";

export function getCurrentLanguage(): string {
	const culture = getCurrentCulture(); // short
	return (
		SUPPORTED_LANGUAGES.find((s) => s.key === culture)?.keyLong ||
		SUPPORTED_LANGUAGES[0].keyLong
	);
}

export function getCookie(cname: string): string | null {
	const name = cname + "=";
	const ca = decodeURIComponent(document.cookie).split(";");
	for (let i = 0; i < ca.length; i++) {
		let c = ca[i];
		while (c.charAt(0) === " ") c = c.substring(1);
		if (c.indexOf(name) === 0) return c.substring(name.length, c.length);
	}
	return null;
}

export function setCookie(
	key: string,
	value: string,
	days?: number,
	sameSite?: string
): void {
	let expires = "";

	if (days) {
		const date = new Date();
		date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
		expires = "; expires=" + date.toUTCString();
	}

	const sameSiteString = sameSite != null ? `;samesite=${sameSite}` : "";

	document.cookie = `${key}=${
		value || ""
	}${expires}; path=/${sameSiteString}`;
}

export function getCurrentCulture(): string {
	// TODO: is this the best way to get current culture ?
	const culture = window.location.pathname.split("/")[1];
	return (
		SUPPORTED_LANGUAGES.find(
			(s) => s.key === culture || s.keyLong === culture
		)?.key ||
		getCookie("lng") ||
		WEBPACK_DEFAULT_CULTURE
	);
}

export function isNullOrWhitespace(
	value: string | undefined | null | void
): boolean {
	if (typeof value === "undefined" || value == null) {
		return true;
	}
	return value.replace(/\s/g, "").length < 1;
}

/** @todo this doesn't seem to be used. */
export function fontResize(): void {
	const d = document.querySelector(".linkbox");

	if (d != null) {
		const a = d.getBoundingClientRect().width;
		const b = a * 0.7;
		const c = a * 0.12;

		const twIcon = d.querySelector<HTMLElement>(".u-tw-icon");
		if (twIcon != null) {
			twIcon.style.fontSize = b.toString();
		}

		const linkBox = d.querySelector<HTMLElement>(".linkbox__link__title");
		if (linkBox != null) {
			linkBox.style.fontSize = c.toString();
		}
	}
}

export function setTabTitle(newTitle: string) {
	if (newTitle === "") {
		console.error("Title can not be empty string");
		return;
	}
	document.title = newTitle;
}

export function ISOToDisplayCode(currencyISO: string): string {
	switch (currencyISO) {
		// British pound
		case "GBP":
			return "£";
		// Swedish, Norwegian, Danish krone
		case "SEK":
		case "NOK":
		case "DKK":
			return "kr";
		// Croatian kuna
		case "HRK":
			return "KN";
		// Bosnia and Hercegovina convertible mark
		case "BAM":
			return "KM";
		// Turkish lira
		case "TRY":
			return "₺";
		case "USD":
			return "$";
		// Namibian dollar
		case "NAD":
			return "N$";
		// Bulgarian lev
		case "BGN":
			return "лв";
		case "EUR":
		default:
			return "€";
	}
}

export function getDisplayedAmount(
	value: number | null,
	currency: string = ISOToDisplayCode(THEME_SETTINGS.defaultCurrency),
	digits: number = 2
): string {
	if (value == null) {
		return "";
	}

	if (!digits) {
		return `${value} ${currency}`;
	}

	return `${value.toFixed(digits)} ${currency}`;
}

export function getDefaultCountryCodeForTheme(): string {
	if (THEME_SETTINGS.defaultCountryFlag) {
		return THEME_SETTINGS.defaultCountryFlag.toUpperCase();
	}
	return "";
}

// if user is on registration domain, after registration (error or success) call this method to redirect user back to home page !
// this will check if user is on registration domain, if it is user must be redirected back to default domain home page
export function redirectUserToHomePageAfterRegistration(): void {
	if (window.location.host.includes("registration")) {
		window.location.href = `${WEBPACK_WEBSITE_URL}/${getCurrentCulture()}/${
			DefaultPath.HOME
		}`;
		return;
	}
	App.state.redirect(`/${getCurrentCulture()}/${DefaultPath.HOME}`);
}

export function getWebApiUrl(): string {
	if (isCapacitorPlatform()) {
		return WEBPACK_WEBSITE_URL;
	}
	return "";
}

export function isTypeOf<T extends object>(
	object: object,
	property: keyof T
): object is T {
	return object && property in object;
}

export function getIsDateToday(date: string) {
	const today = new Date();
	const inputDate = new Date(date);

	const todayString = today.toDateString();
	const dateString = inputDate.toDateString();

	return todayString === dateString;
}

export function getIs24hoursPassed(fromDate?: string) {
	if (!fromDate) {
		return false;
	}

	const timestamp = new Date(fromDate).getTime();
	const now = new Date().getTime();
	const differenceInMilliseconds = now - timestamp;
	const dayInMilliseconds = 86400000;

	return differenceInMilliseconds >= dayInMilliseconds;
}

export function getSVGExtension(): string {
	const { isLightTheme } = App.state.rootStore.themeStore;
	const svgExtension = isLightTheme
		? "--filled--light.svg"
		: "--filled--dark.svg";
	return svgExtension;
}

export function getUserBettingAccountTypeId(): string {
	const { getValues } = useFormContext();

	let bettingAccountTypeId = "";
	const accountType = getValues("userTypes");
	const userType = App.state.rootStore.userAuthStore.getUserAccountType(
		Number(accountType)
	);

	const user = getUserFromLocalStorage(userType);

	if (App.state.rootStore.userAuthStore.userAccounts) {
		for (const entry of App.state.rootStore.userAuthStore.userAccounts) {
			if (
				entry.abrv === "sport-betting-account-online" &&
				user?.id === entry.userId
			) {
				bettingAccountTypeId = entry.accountTypeId;
			}
			if (
				entry.abrv === "sport-betting-account-shop-online" &&
				user?.id === entry.userId
			) {
				bettingAccountTypeId = entry.accountTypeId;
			}
		}
	}

	return bettingAccountTypeId;
}

/**
 * Function for smooth scrolling
 * @param element scrollable area
 * @param targetPosition position to scroll to
 * @param duration scroll duration
 * @param setIsScrolling scrolling progress tracker
 * @param scrollTriggerPoint distance in pixel to trigger scroll animation (default is 0)
 * @param loaderDisplayPoint distance in pixel to trigger scroll loading process
 */
export const scrollToY = (
	element: Element,
	targetPosition: number,
	duration = 1000,
	setIsScrolling: (value: boolean) => void,
	scrollTriggerPoint = 0,
	loaderDisplayPoint = 1000
) => {
	const startPosition = element.scrollTop;
	const distance = targetPosition - startPosition;
	const startTime = performance.now();

	// Animation function to perform the scrolling
	const scrollAnimation = (currentTime: number) => {
		// Trigger loader on scrolling if distance is very large
		if (distance > loaderDisplayPoint || distance < -loaderDisplayPoint) {
			setIsScrolling(true);
		}
		// Calculate the elapsed time since the start of animation
		const elapsedTime = currentTime - startTime;
		// Calculate the progress of the animation (between 0 and 1)
		const progress = Math.min(elapsedTime / duration, 1);
		// Apply easing function to the progress for smoother animation
		/* const easedProgress = easeInOutCubic(progress); */
		// Calculate the new scroll position based on easing
		element.scrollTop = targetPosition;

		// Continue animation if progress is less than 1
		if (progress < 1) {
			requestAnimationFrame(scrollAnimation);
		} else {
			setIsScrolling(false);
		}
	};

	// Easing function for smoother animation
	/* const easeInOutCubic = (t: number): number => {
		return t < 0.5
			? 4 * t * t * t
			: (t - 1) * (2 * t - 2) * (2 * t - 2) + 1;
	}; */

	if (distance > scrollTriggerPoint || distance < -scrollTriggerPoint) {
		// Initiate the animation loop using requestAnimationFrame
		requestAnimationFrame(scrollAnimation);
	} else {
		setIsScrolling(false);
	}
};

export function isGamesTheme(): boolean {
	return (
		WEBPACK_PUBLIC_THEME === "twde-games" ||
		WEBPACK_PUBLIC_THEME === "twde-games-mobile"
	);
}

export const getOpacityClass = (value: number, maxValue: number): string => {
	const intensity = value / maxValue;

	if (intensity <= 0.35) return "opacity-35";
	if (intensity <= 0.55) return "opacity-55";
	if (intensity <= 0.8) return "opacity-80";
	return "opacity-100";
};

export function getWalletTranslationKey(walletAbrv?: string) {
	switch (walletAbrv) {
		case UserAccountType.SportBettingAccountOnline:
			return "USER.WALLETS.ONLINE";
		case UserAccountType.SportBettingAccountShop:
			return "USER.WALLETS.OFFLINE";
		case UserAccountType.SportBettingAccountShopOnline:
			return "USER.WALLETS.SHOP";
		case UserAccountType.CashToDigitalAccount:
			return "USER.WALLETS.CASH_TO_DIGITAL";
		case UserAccountType.CasinoAccount:
			return "USER.WALLETS.CASINO";
		case UserAccountType.LiveCasinoAccount:
			return "USER.WALLETS.LIVE_CASINO";
		case UserAccountType.Games:
			return "USER.WALLETS.GAMES";
		default:
			return "";
	}
}

export function copyTextToClipboard(
	copyValue: string,
	successMessage?: string
) {
	const textarea = document.createElement("textarea");
	textarea.value = copyValue;
	textarea.setAttribute("readonly", "");
	textarea.style.position = "absolute";
	textarea.style.left = "-9999px";

	document.body.appendChild(textarea);

	textarea.select();

	try {
		const isSuccess = document.execCommand("copy");

		if (isSuccess) {
			if (successMessage) {
				App.state.rootStore.notificationStore.success(
					localizationService.t(successMessage)
				);
			} else {
				App.state.rootStore.notificationStore.success(
					localizationService.t("COMMON.COPIED")
				);
			}
		}
	} catch (err) {
		console.error("Copy error:", err);
	}

	document.body.removeChild(textarea);
}
